import { ReactNode, useMemo } from "react";
import { useMatch } from "react-router-dom";
import useExternalBuckets from "files/hooks/useExternalBuckets";
import { BASE_DIRECTORIES, resolveRouterPath, useCurrentPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { AwsIcon } from "~/assets/icons";
import { cn } from "~/lib/utils";
import { FileRow, OnRowClickParams } from "./file-row";
import { Label } from "./label";
import { ListCard } from "./list-card";

const useUrlNavigation = ({ loading }: { loading: boolean }) => {
  const directoryPath = useCurrentPath();
  const isGlobalFileContext = !useMatch("/workbenches/:workbenchId/files/*");
  const isRoot = !directoryPath;
  const { data } = useExternalBuckets();
  const showOtherLocations = !loading && isRoot && !!data?.length;

  return { isGlobalFileContext, directoryPath, showOtherLocations, externalBuckets: data };
};

const DirectoryHeader = ({ hideAllActions }: { hideAllActions?: boolean }) => {
  return (
    <Label className="flex rounded-t-lg bg-white gap-8 px-5 py-3 font-medium border-b border-gray-300 text-[13px] text-gray-900/50">
      <span className="grow shrink basis-0 text-nowrap">Name</span>
      <span className="w-[100px] text-end">Size</span>
      <span className={cn("w-[236px]", hideAllActions && "w-fit")}>Updated</span>
    </Label>
  );
};

interface OtherLocation {
  fileKey: string;
  description: string;
}

type DirectoryProps = {
  files: FileInfo[];
  loading: boolean;
  hideAllActions?: boolean;
  className?: string;
  listCardClassName?: string;
  showExternalBuckets?: boolean;
  error?: ReactNode;
} & (
  | {
      navigation: "url-based";
      currentPath?: undefined;
      otherLocations?: undefined;
      onRowClick?: undefined;
      selectedFileKey?: undefined;
    }
  | {
      navigation: "in-memory";
      currentPath: string;
      onRowClick?: (params: OnRowClickParams) => void;
      selectedFileKey?: string;
      /** These are only shown if `loading === false` */
      otherLocations?: OtherLocation[];
    }
);

const Directory = ({
  currentPath,
  files,
  otherLocations: externalLocations,
  loading,
  navigation,
  hideAllActions,
  selectedFileKey,
  onRowClick,
  className,
  listCardClassName,
  showExternalBuckets,
  error,
}: DirectoryProps) => {
  const { directoryPath, isGlobalFileContext, externalBuckets, showOtherLocations } =
    useUrlNavigation({
      loading,
    });
  const isRoot =
    navigation === "url-based" ? !directoryPath : currentPath === "" || currentPath === "/";

  const rows = useMemo(() => {
    const backNav = [];

    if (!isRoot) {
      backNav.push(
        <FileRow
          key="back"
          fileKey=".."
          currentPath={currentPath}
          hideActions
          hideAllActions={hideAllActions}
          navigation={navigation}
          onRowClick={onRowClick}
          selectedFileKey={selectedFileKey}
        />
      );
    }

    return [
      ...backNav,
      ...files.map((node) => {
        // If existing, use directory title/subtitle overrides
        const resolvedPath = resolveRouterPath(node.key, isGlobalFileContext);
        const overrides = BASE_DIRECTORIES[resolvedPath];
        return (
          <FileRow
            key={node.key}
            fileKey={node.key}
            currentPath={currentPath}
            navigation={navigation}
            hideAllActions={hideAllActions}
            fileMetadata={{
              subtitle: overrides?.subtitle,
              type: node.type,
              size: node.size,
              pendingAction: node.pending_action,
              updatedAt: node.updated_at,
              s3Uri: node.s3_uri,
              badge: node.badge,
            }}
            onRowClick={onRowClick}
            selectedFileKey={selectedFileKey}
          />
        );
      }),
    ];
  }, [
    isRoot,
    files,
    currentPath,
    hideAllActions,
    navigation,
    onRowClick,
    selectedFileKey,
    isGlobalFileContext,
  ]);

  const otherLocations: OtherLocation[] | undefined =
    showExternalBuckets && isRoot
      ? externalBuckets?.map(({ bucket_name, description }) => ({
          fileKey: bucket_name,
          description,
        }))
      : externalLocations;

  if (error) {
    return (
      <p className="text-destructive mb-2 text-sm">
        There was an error loading the S3 files. Please try again later.
      </p>
    );
  }

  return (
    <div className={cn("flex flex-col gap-8", className)}>
      <ListCard
        className={cn("gap-1 flex flex-col", listCardClassName)}
        header={<DirectoryHeader hideAllActions={hideAllActions} />}
        loading={loading}
        placeholder={{ className: "h-[34px]" }}
      >
        {rows}
      </ListCard>
      {showOtherLocations && otherLocations && (
        <ListCard
          className={cn("gap-1", listCardClassName)}
          header={
            <Label className="px-5 py-3 text-[13px] text-gray-900/50 font-medium border-b border-gray-300">
              Other Locations
            </Label>
          }
        >
          {otherLocations.map(({ fileKey, description }) => (
            <FileRow
              key={fileKey}
              fileKey={fileKey}
              currentPath={currentPath}
              navigation={navigation}
              hideAllActions={hideAllActions}
              hideActions
              fileMetadata={{
                subtitle: description,
                badge: <AwsIcon width={24} />,
              }}
              onRowClick={onRowClick}
              selectedFileKey={selectedFileKey}
            />
          ))}
        </ListCard>
      )}
    </div>
  );
};

export { Directory, type OtherLocation, type DirectoryProps };
